import React from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useRouteMatch } from 'react-router';
import styled from 'styled-components';
import { AttachmentType } from 'api/attachments/types';
import { ProjectId, TaskId } from 'api/models';
import { Loading } from 'components/atoms/loading';
import { ImagePreview } from 'components/organisms/image-preview/image-preview';
import { PreviewHeader } from 'components/organisms/image-preview/preview-header/image-preview-header';
import { VersionList } from 'components/organisms/image-preview/version-list';
import { RoutePaths } from 'constant';
import { getProjectFileGroupsQueryKey } from 'queries/project-files';
import { getTaskDetailQueryKey } from 'queries/task';
import { ConditionalSocketSubscriber } from '../conditinal-socket-subscriber';
import { useAttachmentDetails } from './use-attachment-details';

const CommentAndPreviewWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100% - 60px);
  width: 100%;
  background-color: var(--coolest-gray-10);
  overflow: hidden;
`;

const LoadingContainer = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: var(--zindex-popover-high);
  background-color: var(--white-90);
`;

export const WorkspaceWrapper = styled.div`
  height: 100vh;
  width: 100%;
  overflow: hidden;
`;

export const AttachmentWorkspace = () => {
  const match = useRouteMatch([RoutePaths.attachmentWorkspace]);
  const { attachmentId } = match?.params as {
    attachmentId: number;
  };
  const {
    latestVersion,
    setLatestVersion,
    selectedVersion,
    setSelectedVersion,
    versionFileList,
    setVersionFileList,
    isChatSectionVisible,
    setChatSectionVisiblity,
    isVersionSectionVisible,
    setVersionSectionVisiblity,
    isLoadingForAllAttachment,
    leftAttachment,
    setLeftAttachment,
    rightAttachment,
    setRightAttachment,
    onOpenParentTask,
  } = useAttachmentDetails(attachmentId);
  const history = useHistory();
  const queryClient = useQueryClient();

  const onClose = () => {
    if (latestVersion && latestVersion.sourceId) {
      if (latestVersion.sourceType === AttachmentType.Task) {
        onOpenParentTask();
        queryClient.invalidateQueries(
          getTaskDetailQueryKey(
            TaskId(latestVersion.projectId, latestVersion.sourceId),
          ),
        );
      }
      if (latestVersion.sourceType === AttachmentType.Folder) {
        history.goBack();
        queryClient.refetchQueries(
          getProjectFileGroupsQueryKey(ProjectId(latestVersion.projectId)),
        );
      }
    } else {
      history.goBack();
    }
  };

  const onDownload = () => {
    const a = document.createElement('a');

    a.setAttribute(
      'href',
      selectedVersion?.location || selectedVersion?.thumbLocation || '',
    );
    a.setAttribute('target', '_blank');
    a.click();
  };

  if (selectedVersion && latestVersion) {
    return (
      <WorkspaceWrapper>
        {selectedVersion.projectId ? (
          <ConditionalSocketSubscriber projectId={selectedVersion?.projectId} />
        ) : null}
        <PreviewHeader
          fileList={versionFileList}
          isLoadingForAllAttachment={isLoadingForAllAttachment}
          isChatSectionVisible={isChatSectionVisible}
          onChatSectionToggle={() => {
            setChatSectionVisiblity((prev) => !prev);
          }}
          isVersionSectionVisible={isVersionSectionVisible}
          onVersionSectionToggle={() => {
            setVersionSectionVisiblity((prev) => !prev);
          }}
          fileName={selectedVersion.name || ''}
          onClose={() => onClose?.()}
          onOpenParentTask={onOpenParentTask}
          onDownload={() => onDownload?.()}
          src={document.location.href}
          selectedVersion={selectedVersion}
          leftAttachment={leftAttachment}
          setLeftAttachment={setLeftAttachment}
          rightAttachment={rightAttachment}
          setRightAttachment={setRightAttachment}
        />
        <CommentAndPreviewWrapper>
          {versionFileList && (
            <VersionList
              isVersionSectionVisible={isVersionSectionVisible}
              latestVersion={latestVersion}
              versionFileList={versionFileList}
              setVersionFileList={setVersionFileList}
              selectedVersion={selectedVersion}
              setSelectedVersion={(attach) => setSelectedVersion(attach)}
              setLatestVersion={setLatestVersion}
              setLeftAttachment={setLeftAttachment}
              setRightAttachment={setRightAttachment}
            />
          )}
          <ImagePreview
            attachmentId={selectedVersion.attachmentId}
            data={selectedVersion}
            isChatSectionVisible={isChatSectionVisible}
            setChatSectionVisiblity={(value) => setChatSectionVisiblity(value)}
            src={selectedVersion.location}
          />
        </CommentAndPreviewWrapper>
      </WorkspaceWrapper>
    );
  }

  return (
    <LoadingContainer>
      <Loading size="large" />
    </LoadingContainer>
  );
};
